import React, { useState } from "react";
import { Handshake, MapPin, Tag, Wrench, Megaphone, MessageCircle, Send } from "lucide-react";
import { company, dealerBenefits } from "../mock";
import Reveal from "./Reveal";
import { createEnquiry } from "../api";
import { toast } from "../hooks/use-toast";

const iconMap = [Tag, Wrench, Megaphone, MapPin];
const waNumber = (company.whatsapp || "").replace(/[^0-9]/g, "");

export default function Dealership() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Please fill name, email and phone", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const message = `DEALERSHIP ENQUIRY | Phone: ${form.phone} | City: ${form.city} | ${form.message}`;
      await createEnquiry({ name: form.name, email: form.email, message });
      toast({ title: "Application received!", description: "Thanks " + form.name + ", our team will contact you about the dealership." });
      setForm({ name: "", email: "", phone: "", city: "", message: "" });
    } catch (err) {
      toast({ title: "Could not submit", description: "Please check your email and try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="dealership" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm text-[color:var(--brand-blue)] text-xs font-semibold mb-4">
            <Handshake size={14} /> Partner with us
          </div>
          <h2 className="section-title text-3xl md:text-5xl mb-4">Become a Dealer</h2>
          <p className="text-slate-600">Join the growing Bhawani Kitchen Equipment network. Bring premium commercial kitchen solutions to your region with strong margins and full support.</p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="grid sm:grid-cols-2 gap-5">
            {dealerBenefits.map((b, i) => {
              const Icon = iconMap[i % iconMap.length];
              return (
                <Reveal key={b.title} delay={i * 80}>
                  <div className="card-hover h-full bg-white rounded-2xl border border-slate-100 p-6">
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--brand-ice)" }}>
                      <Icon size={22} className="text-[color:var(--brand-blue)]" />
                    </div>
                    <h3 className="font-head font-semibold text-lg mb-1.5" style={{ color: "var(--brand-navy)" }}>{b.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={120}>
            <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8">
              <h3 className="font-head font-bold text-xl mb-5" style={{ color: "var(--brand-navy)" }}>Dealership Application</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[color:var(--brand-blue)] transition-colors" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[color:var(--brand-blue)] transition-colors" />
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[color:var(--brand-blue)] transition-colors" />
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City / Region" className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[color:var(--brand-blue)] transition-colors" />
              </div>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Tell us about your business..." className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[color:var(--brand-blue)] transition-colors mb-4 resize-none" />
              <div className="flex flex-col sm:flex-row gap-3">
                <button type="submit" disabled={sending} className="brand-btn flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold disabled:opacity-70">
                  {sending ? "Submitting..." : "Submit Application"} <Send size={17} />
                </button>
                <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Bhawani Kitchen Equipment, I'm interested in becoming a dealer. Please share the details.")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-white bg-[#25D366] hover:bg-[#1ebe57] transition-colors">
                  <MessageCircle size={17} /> WhatsApp
                </a>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
